import React, { useState } from 'react';
import { useSpc } from '../../context/SpcContext';
import { Reveal } from '../ui';
import { MultiImagePicker } from '../ImagePicker';
import { formatDisplay } from '../../lib/dates';
import { Target, Trophy, Clock, CheckCircle2, Award, Trash2, ChevronRight } from 'lucide-react';

function periodLabel(p) {
  return { day: 'Daily', week: 'Weekly', month: 'Monthly', 'one-off': 'One-off' }[p] || 'Ongoing';
}

function fmtEndsIn(iso) {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'Ended';
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  if (days >= 1) return `Ends in ${days} day${days === 1 ? '' : 's'}`;
  return `Ends in ${hours}h`;
}

function SubmitForm({ challenge, onDone }) {
  const { submitChallenge, toast } = useSpc();
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const canSubmit = !busy && (photos.length > 0 || note.trim().length >= 6);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast('Add a photo or write what you did (at least a few words) so a coach can verify it');
      return;
    }
    setBusy(true);
    const ok = await submitChallenge(challenge.id, { note: note.trim(), photos });
    setBusy(false);
    if (ok) onDone && onDone();
  };

  return (
    <form className="form" onSubmit={submit} data-testid={`challenge-submit-form-${challenge.id}`}>
      <div className="notice"><strong>Send proof.</strong> For anything fitness-related, a screenshot from <strong>Strava, Garmin or Apple Fitness</strong> is best — it shows the distance, pace and route. A regular photo of you doing the thing works too. If you can't grab a screenshot, drop a short note (where, when, how far) and an admin will approve it.</div>
      <div className="notice soft" data-testid="challenge-single-entry-note"><strong>One entry only, please.</strong> Only send this once your distance or challenge is fully completed — not partway through. If you've already sent one for this challenge, wait for it to be reviewed rather than sending another.</div>
      <label>What did you do? <span style={{ color: '#a4a8ad', fontWeight: 400 }}>(add a screenshot/photo or a short note — at least one is required)</span>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. 8km @ 5:30am along the canal — Strava link in note" data-testid="challenge-note" />
      </label>
      <MultiImagePicker photos={photos} setPhotos={setPhotos} max={4} label="Screenshot or photo proof (up to 4)"
        hint="Add up to 4 photos or screenshots as proof." toast={toast} testidPrefix="challenge-photo" />
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" disabled={!canSubmit} data-testid={`challenge-submit-btn-${challenge.id}`}><CheckCircle2 size={15} /> {busy ? 'Sending…' : 'Send for approval'}</button>
        <button type="button" className="btn secondary" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

function ResubmitForm({ submission, challenge }) {
  const { resubmitChallenge, toast } = useSpc();
  const [note, setNote] = useState(submission.note || '');
  const [photos, setPhotos] = useState(submission.photos && submission.photos.length ? submission.photos : (submission.photo ? [submission.photo] : []));
  const [busy, setBusy] = useState(false);

  const canSubmit = !busy && (photos.length > 0 || note.trim().length >= 6);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast('Add a photo or write what you did (at least a few words) so a coach can verify it');
      return;
    }
    setBusy(true);
    await resubmitChallenge(submission.id, { note: note.trim(), photos });
    setBusy(false);
  };

  return (
    <form className="form" onSubmit={submit} data-testid={`challenge-resubmit-form-${submission.id}`} style={{ marginTop: 8 }}>
      {submission.adminComment && <div className="notice" data-testid={`challenge-needs-changes-note-${submission.id}`}><strong>Coach note:</strong> {submission.adminComment}</div>}
      <label>Fix your entry
        <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid={`resubmit-note-${submission.id}`} />
      </label>
      <MultiImagePicker photos={photos} setPhotos={setPhotos} max={4} label="Screenshot or photo proof (up to 4)" toast={toast} testidPrefix={`resubmit-photo-${submission.id}`} />
      <button className="btn" disabled={!canSubmit} data-testid={`resubmit-submit-btn-${submission.id}`}><CheckCircle2 size={15} /> {busy ? 'Sending…' : 'Send back for review'}</button>
    </form>
  );
}

function SubmissionPhotos({ photos, size = 72 }) {
  const list = (photos || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
      {list.map((p, i) => (
        <img key={i} src={p} alt="entry" className="challenge-entry-thumb" style={{ width: size, height: size }} />
      ))}
    </div>
  );
}

function ChallengeCard({ c, role }) {
  const { purgeChallenge, challengePhotoUrl } = useSpc();
  const [expanded, setExpanded] = useState(false);
  const pct = Math.min(100, Math.round((c.myApproved / (c.target || 1)) * 100));
  const isActive = c.status === 'active';

  return (
    <Reveal className="card challenge-card" data-testid={`challenge-${c.id}`}>
      {c.hasPhoto && <div className="challenge-photo"><img src={challengePhotoUrl(c.id, c.photoV)} alt={c.title} /></div>}
      <div className="challenge-head">
        <span className="pill yellow"><Target size={12} /> {periodLabel(c.period)}</span>
        {isActive ? <span className="pill"><Clock size={12} /> {fmtEndsIn(c.endsAt)}</span> : <span className="pill">Ended</span>}
        {c.pointsAward > 0 && <span className="pill green"><Award size={12} /> +{c.pointsAward} pts</span>}
        {!isActive && role === 'admin' && (
          <button
            type="button"
            className="btn ghost"
            style={{ marginLeft: 'auto', padding: '4px 8px' }}
            data-testid={`challenge-delete-${c.id}`}
            title="Remove from Past challenges & winners"
            onClick={() => { if (window.confirm(`Permanently remove "${c.title}" from Past challenges & winners? This can't be undone.`)) purgeChallenge(c.id); }}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
      <h3 className="challenge-title">{c.title}</h3>
      {c.description && <p className="challenge-desc">{c.description}</p>}

      <div className="challenge-progress" data-testid={`challenge-progress-${c.id}`}>
        <div className="challenge-progress-bar"><div style={{ width: pct + '%' }} /></div>
        <div className="challenge-progress-meta">
          <span>{c.myApproved}/{c.target} {c.unit || 'completions'}</span>
          {c.myPending > 0 && <span className="pill yellow" data-testid={`challenge-pending-${c.id}`}>{c.myPending} awaiting review</span>}
          {c.myNeedsChanges > 0 && <span className="pill red" data-testid={`challenge-needs-changes-${c.id}`}>Needs changes</span>}
        </div>
      </div>

      {c.status === 'ended' && c.winnerName && (
        <div className="challenge-winner" data-testid={`challenge-winner-${c.id}`}><Trophy size={15} /> Winner: <strong>{c.winnerName}</strong></div>
      )}

      {isActive && !expanded && c.myPending === 0 && c.myNeedsChanges === 0 && (
        <button className="btn challenge-cta" onClick={() => setExpanded(true)} data-testid={`challenge-open-${c.id}`}>
          <span className="challenge-cta-label"><span className="challenge-cta-icon"><CheckCircle2 size={16} /></span>Log a completion</span>
          <ChevronRight size={17} className="challenge-cta-arrow" />
        </button>
      )}
      {isActive && !expanded && (c.myPending > 0 || c.myNeedsChanges > 0) && (
        <div className="notice soft" data-testid={`challenge-blocked-note-${c.id}`}>
          {c.myNeedsChanges > 0 ? "A coach asked for a small change on your entry below — fix it and send it back." : "You've got an entry awaiting review — see below. Sit tight before sending another."}
        </div>
      )}
      {isActive && expanded && (
        <SubmitForm challenge={c} onDone={() => setExpanded(false)} />
      )}

      {c.mySubmissions && c.mySubmissions.length > 0 && (
        <details className="challenge-history" open={c.myNeedsChanges > 0}>
          <summary>Your entries ({c.mySubmissions.length})</summary>
          <div className="list" style={{ marginTop: 8 }}>
            {c.mySubmissions.map(s => (
              <div className="item" key={s.id} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <div>
                    <strong>{formatDisplay(s.createdAt) || new Date(s.createdAt).toLocaleDateString()}</strong>
                    {s.note && <div className="meta">{s.note}</div>}
                    <SubmissionPhotos photos={s.photos && s.photos.length ? s.photos : (s.photo ? [s.photo] : [])} size={56} />
                  </div>
                  <span className={`pill ${s.status === 'approved' ? 'green' : s.status === 'rejected' ? 'red' : s.status === 'needs_changes' ? 'yellow' : ''}`} data-testid={`submission-status-${s.id}`}>
                    {s.status === 'needs_changes' ? 'Needs changes' : s.status}
                  </span>
                </div>
                {s.status === 'needs_changes' && <ResubmitForm submission={s} challenge={c} />}
              </div>
            ))}
          </div>
        </details>
      )}
    </Reveal>
  );
}

export default function Challenges() {
  const { state, role } = useSpc();
  const list = state.challenges || [];
  const active = list.filter(c => c.status === 'active');
  const ended = list.filter(c => c.status !== 'active');

  return (
    <div data-testid="challenges-view">
      <Reveal className="section-head">
        <div><div className="eyebrow">Challenges</div><h2>SPC Challenges</h2><p>Anything from a sunset run to a silly-o'clock effort. Log what you did and an admin will tick it off.</p></div>
      </Reveal>

      {active.length === 0 && ended.length === 0 && (
        <div className="empty" data-testid="challenges-empty"><Target size={26} /><p>No challenges yet. Ask an admin to set one — daily, weekly, monthly or one-off.</p></div>
      )}

      {active.length > 0 && <>
        <h3 className="challenge-section-title">Active</h3>
        <div className="challenge-grid">{active.map(c => <ChallengeCard key={c.id} c={c} role={role} />)}</div>
      </>}

      {ended.length > 0 && <>
        <h3 className="challenge-section-title" style={{ marginTop: 24 }}>Past challenges & winners</h3>
        <div className="challenge-grid">{ended.map(c => <ChallengeCard key={c.id} c={c} role={role} />)}</div>
      </>}
    </div>
  );
}
